import type { BlockType } from '@unifyapps/defs/types/block';
import type { useRegistryContext } from '../../../context/RegistryProvider';
import type { createGlobalStateStoreSelectors } from '../selectors';
import type { BaseGlobalStateStoreProviderProps } from '../types';
import { useSyncComponentOverrides } from './useSyncComponentOverrides';
import { useSyncPageInputs } from './useSyncPageInputs';
import { useSyncPageParams } from './useSyncPageParams';

type Store = ReturnType<typeof createGlobalStateStoreSelectors>;
type Registry = ReturnType<typeof useRegistryContext>['registry'];
// Wider than base provider's prop so per-instance callers can pass
// device-resolved block maps (may contain undefined entries).
type BlocksMap = Record<string, BlockType | undefined>;

/**
 * Composes every prop->store sync hook the global state store needs. Used by
 * BOTH the base `GlobalStateStoreProvider` (main page) and
 * `useSyncInstanceGlobalState` (per-instance: Module / TemplateComponent).
 *
 * Add new prop->store sync hooks here exactly once — both base and every
 * page instance get them automatically. Without this single source of truth
 * we'd have to remember to wire each new hook into two providers and any
 * future per-instance call sites separately.
 */
export function useSyncGlobalStateProps({
  store,
  blocks,
  pageInputs,
  pageParams,
  componentOverrides,
  registry,
  getDependencyGraphActions,
}: {
  // null when the per-instance caller has not yet resolved the instance store
  // (page context absent or instance not yet registered). Inner hooks
  // short-circuit on null so this composition can be called unconditionally.
  store: Store | null;
  blocks: BlocksMap | undefined;
  pageInputs: BaseGlobalStateStoreProviderProps['pageInputs'];
  pageParams: BaseGlobalStateStoreProviderProps['pageParams'];
  componentOverrides: BaseGlobalStateStoreProviderProps['componentOverrides'];
  registry: Registry;
  getDependencyGraphActions: BaseGlobalStateStoreProviderProps['getDependencyGraphActions'];
}) {
  useSyncPageInputs({ store, pageInputs, getDependencyGraphActions });
  useSyncPageParams({ store, pageParams, getDependencyGraphActions });

  useSyncComponentOverrides({
    store,
    blocks,
    componentOverrides,
    registry,
    getDependencyGraphActions,
  });
}
